// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import {
  cvFileHasStored,
  downloadCvFile,
  fmtDate,
  fmtSize,
  fileIcon,
  getCoolingStatus,
  talentPoolCvDownloadUrl,
  TP_SOURCE_OPTIONS,
} from "@/legacy/helpersModule";
import { Modal } from "@/shared/components/ui/Modal";
import { ResumePreviewModal } from "@/shared/components/ResumePreviewModal";
import {
  fetchTalentPoolPage,
  fetchTalentPoolById,
  exportTalentPoolReport,
} from "@/shared/api/talentPoolApi";
import { Button, Field, Input, Select, Skeleton, useToast } from "@/shared/components/ui";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";

const PAGE_SIZE = 50;

const EMPTY_FILTERS = {
  role: "",
  skill: "",
  minExp: "",
  maxExp: "",
  location: "",
  source: "",
  keyword: "",
  fromDate: "",
  toDate: "",
};

export function TalentPoolBrowse({
  jobs,
  candidates,
  resolveExistingByEmail,
  onMapToJob,
  onLogAudit,
  onBack,
  coolingMonths,
}) {
  const toast = useToast();
  useEscapeKey(onBack);
  const cm = typeof coolingMonths === "number" ? coolingMonths : 3;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filtersDebounced, setFiltersDebounced] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mapJobId, setMapJobId] = useState("");
  const [mapping, setMapping] = useState(false);
  const [resumePreview, setResumePreview] = useState(null);
  const [list, setList] = useState({ items: [], total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFiltersDebounced(filters), 350);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [filtersDebounced]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const out = await fetchTalentPoolPage({
        page,
        limit: PAGE_SIZE,
        ...filtersDebounced,
      });
      setList({
        items: out.items || [],
        total: out.total ?? 0,
        totalPages: out.totalPages ?? 1,
      });
    } catch (e) {
      console.error(e);
      setListError("Could not load talent pool.");
      setList({ items: [], total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, filtersDebounced]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const hasFilters = Object.values(filters).some((v) => v !== "");

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportTalentPoolReport(filtersDebounced);
    } catch (e) {
      console.error(e);
      toast.show({ tone: "danger", title: "Export failed", message: "Could not export talent pool report. Please try again." });
    } finally {
      setExporting(false);
    }
  };

  const loadDetail = async (entry) => {
    setDetailLoading(true);
    try {
      const full = await fetchTalentPoolById(entry.id);
      setSelected(full);
      onLogAudit("VIEW_TP_PROFILE", full.id, `Viewed ${full.name}`);
      return full;
    } catch (e) {
      console.error(e);
      toast.show({ tone: "danger", title: "Load failed", message: "Could not load profile details." });
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const view = (e) => {
    void loadDetail(e);
  };

  const hasCv = (e) => cvFileHasStored(e?.cvFile, e?.hasCv);

  const download = async (e) => {
    let fileEntry = e;
    if (!cvFileHasStored(e?.cvFile, e?.hasCv)) {
      const full = await fetchTalentPoolById(e.id);
      if (!cvFileHasStored(full?.cvFile, full?.hasCv)) return;
      fileEntry = full;
    }
    downloadCvFile(fileEntry.cvFile, { talentPoolId: fileEntry.id });
    onLogAudit("DOWNLOAD_TP_CV", fileEntry.id, `Downloaded ${fileEntry.name}'s CV`);
  };

  const viewResume = async (e) => {
    let fileEntry = e;
    if (!cvFileHasStored(e?.cvFile, e?.hasCv)) {
      const full = await fetchTalentPoolById(e.id);
      if (!cvFileHasStored(full?.cvFile, full?.hasCv)) return;
      fileEntry = full;
    }
    setResumePreview({
      dataUrl: fileEntry.cvFile.dataUrl,
      fetchUrl: fileEntry.cvFile.dataUrl ? undefined : talentPoolCvDownloadUrl(fileEntry.id),
      fileName: fileEntry.cvFile.name,
      ext: fileEntry.cvFile.ext,
      mime: fileEntry.cvFile.mime,
      cvText: fileEntry.cvText || "",
    });
    onLogAudit("VIEW_TP_CV", fileEntry.id, `In-app preview ${fileEntry.name}'s CV`);
  };

  const handleMap = async () => {
    if (!mapJobId || !selected) return;
    const job = jobs.find((j) => j.id === mapJobId);
    let existing = candidates.find(
      (c) => c.email.toLowerCase() === selected.email.toLowerCase(),
    );
    if (!existing && resolveExistingByEmail) {
      try {
        existing = await resolveExistingByEmail(selected.email);
      } catch (e) {
        console.error(e);
      }
    }
    if (existing) {
      const status = getCoolingStatus(existing.applicationHistory, mapJobId, cm);
      if (status.pendingInterview) {
        toast.show({
          tone: "warning",
          title: "Cannot map",
          message: `${existing.name} already has an active application (interview not completed).`,
        });
        return;
      }
      if (!status.canApply) {
        toast.show({
          tone: "warning",
          title: "Cannot map",
          message: `${existing.name} is in cooling period (${status.daysRemaining}d remaining).`,
        });
        return;
      }
    }
    setMapping(true);
    try {
      await onMapToJob(selected, mapJobId);
      onLogAudit("MAP_TO_JOB", selected.id, `Mapped ${selected.name} → ${job?.title}`);
      toast.show({ tone: "success", title: "Mapped to job", message: `${selected.name} mapped to "${job?.title}".` });
      setMapping(false);
      setMapJobId("");
      setSelected(null);
      void loadPage();
    } catch (_e) {
      setMapping(false);
    }
  };

  const items = list.items;

  return (
    <>
      {resumePreview ? (
        <ResumePreviewModal
          dataUrl={resumePreview.dataUrl}
          fetchUrl={resumePreview.fetchUrl}
          fileName={resumePreview.fileName}
          ext={resumePreview.ext}
          mime={resumePreview.mime}
          cvText={resumePreview.cvText}
          onClose={() => setResumePreview(null)}
        />
      ) : null}
      <div className="min-h-dvh safe-bottom bg-surface-alt">
        <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex items-center gap-3 flex-wrap">
          <button onClick={onBack} className="text-ink-400 hover:text-ink-900 text-sm">
            ← Back
          </button>
          <span className="font-bold">Talent Pool</span>
          <span className="text-xs text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full">
            {list.total} profiles
          </span>
          <span className="text-xs text-ink-400 ml-auto"></span>
        </div>
        {selected && (
          <Modal
            title={selected.name}
            onClose={() => {
              setSelected(null);
              setMapping(false);
              setMapJobId("");
              setResumePreview(null);
            }}
            wide
          >
            {detailLoading ? (
              <p className="text-sm text-ink-400 py-8 text-center">Loading profile…</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {selected.phone && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Phone</p>
                      <p>{selected.phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-ink-400 font-bold">Email</p>
                    <p>{selected.email}</p>
                  </div>
                  {selected.location && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Current city</p>
                      <p>{selected.location}</p>
                    </div>
                  )}
                  {selected.preferredCity1 && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Preferred city 1</p>
                      <p>{selected.preferredCity1}</p>
                    </div>
                  )}
                  {selected.preferredCity2 && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Preferred city 2</p>
                      <p>{selected.preferredCity2}</p>
                    </div>
                  )}
                  {selected.preferredCity3 && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Preferred city 3</p>
                      <p>{selected.preferredCity3}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-ink-400 font-bold">Experience</p>
                    <p>{selected.experience} yrs</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-400 font-bold">Submitted</p>
                    <p>{fmtDate(selected.submittedAt)}</p>
                  </div>
                  {selected.qualification && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Qualification</p>
                      <p>{selected.qualification}</p>
                    </div>
                  )}
                  {selected.currentCtc && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Current CTC</p>
                      <p>{selected.currentCtc}</p>
                    </div>
                  )}
                  {selected.currentEmployer && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-ink-400 font-bold">Current employer</p>
                      <p>{selected.currentEmployer}</p>
                    </div>
                  )}
                  {selected.source && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Source</p>
                      <p>{selected.source}</p>
                    </div>
                  )}
                  {selected.applicationDate && (
                    <div>
                      <p className="text-xs text-ink-400 font-bold">Application date</p>
                      <p>{selected.applicationDate}</p>
                    </div>
                  )}
                  {selected.coolingPeriod && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-ink-400 font-bold">Notice period</p>
                      <p>{selected.coolingPeriod}</p>
                    </div>
                  )}
                </div>
                {selected.desiredRoles?.length > 0 && (
                  <div>
                    <p className="text-xs text-ink-400 font-bold mb-2">Desired Roles</p>
                    <div className="flex gap-1 flex-wrap">
                      {selected.desiredRoles.map((r) => (
                        <span
                          key={r}
                          className="text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded-lg"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selected.skills?.length > 0 && (
                  <div>
                    <p className="text-xs text-ink-400 font-bold mb-2">Skills</p>
                    <div className="flex gap-1 flex-wrap">
                      {selected.skills.map((s) => (
                        <span
                          key={s}
                          className="text-xs bg-slate-100 text-ink-600 px-2 py-1 rounded-lg"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selected.keywords && (
                  <div>
                    <p className="text-xs text-ink-400 font-bold mb-1">Notes</p>
                    <p className="text-sm text-ink-600">{selected.keywords}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-ink-400 font-bold mb-1">Resume</p>
                  {cvFileHasStored(selected.cvFile, selected.hasCv) ? (
                    <div className="bg-surface-alt border border-line rounded-xl p-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm min-w-0">
                        <span className="text-2xl shrink-0">
                          {fileIcon(selected.cvFile.ext)}
                        </span>
                        <div className="min-w-0">
                          <p className="font-bold text-ink-900 truncate">
                            {selected.cvFile.name}
                          </p>
                          <p className="text-xs text-ink-400">
                            {selected.cvFile.ext.toUpperCase()} · {fmtSize(selected.cvFile.size)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 w-full sm:w-auto">
                        <Button type="button" variant="secondary" size="sm" className="w-full sm:w-auto" onClick={() => viewResume(selected)}>
                          View
                        </Button>
                        <Button type="button" size="sm" className="w-full sm:w-auto" onClick={() => download(selected)}>
                          ⬇ Download
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-ink-400 bg-surface-alt border border-line rounded-xl p-3">
                      No CV on file for this profile.
                    </p>
                  )}
                </div>
                <div className="border-t border-line pt-4">
                  {!mapping ? (
                    <Button fullWidth size="lg" onClick={() => setMapping(true)}>
                      → Map to Job
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <Select
                        value={mapJobId}
                        onChange={(e) => setMapJobId(e.target.value)}
                      >
                        <option value="">— Choose role —</option>
                        {jobs.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.title} · {j.location}
                          </option>
                        ))}
                      </Select>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          variant="secondary"
                          className="w-full sm:flex-1"
                          onClick={() => {
                            setMapping(false);
                            setMapJobId("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="w-full sm:flex-1"
                          onClick={handleMap}
                          disabled={!mapJobId}
                        >
                          Confirm
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Modal>
        )}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x">
          <h1 className="text-xl sm:text-3xl font-black text-ink-900 mb-1">Talent Pool</h1>
          <p className="text-ink-400 mb-6">Search, view, download, and map talent pool CVs.</p>
          <div className="bg-white rounded-2xl border border-line shadow-sm p-5 mb-6">
            <h2 className="text-xs font-black text-ink-400 uppercase mb-3">🔍 Filters</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                ["Role", "role", "text", "Embryologist"],
                ["Skill", "skill", "text", "ICSI"],
                ["Location", "location", "text", "Mumbai"],
                ["Keyword", "keyword", "text", "Free text"],
                ["Min Exp", "minExp", "number", ""],
                ["Max Exp", "maxExp", "number", ""],
                ["From Date", "fromDate", "date", ""],
                ["To Date", "toDate", "date", ""],
              ].map(([l, k, t, p]) => (
                <Field key={k} label={l}>
                  <Input
                    type={t}
                    value={filters[k]}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, [k]: e.target.value }))
                    }
                    placeholder={p}
                  />
                </Field>
              ))}
              <div className="col-span-2 flex items-end gap-3">
                <Field label="Source" className="flex-1 min-w-0">
                  <Select
                    value={filters.source}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, source: e.target.value }))
                    }
                  >
                    <option value="">All</option>
                    {TP_SOURCE_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleExport()}
                  loading={exporting}
                  className="shrink-0 whitespace-nowrap"
                >
                  Export Candidate details
                </Button>
              </div>
            </div>
            {hasFilters && (
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="mt-3 text-xs text-brand-500 font-bold"
              >
                Clear all ✕
              </button>
            )}
          </div>
          <p className="text-sm font-bold text-ink-600 mb-3">
            {loading ? "Loading…" : `${list.total} ${list.total === 1 ? "profile" : "profiles"}`}
            {hasFilters && !loading ? ` (page ${page} of ${list.totalPages})` : ""}
          </p>
          {listError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-4 text-sm">
              {listError}
            </div>
          ) : null}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-line p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Skeleton className="w-11 h-11 rounded-xl" />
                    <div className="flex-1">
                      <Skeleton className="h-3 w-28 mb-2" />
                      <Skeleton className="h-3 w-36" />
                    </div>
                  </div>
                  <Skeleton className="h-3 w-full mb-2" />
                  <Skeleton className="h-8 w-full rounded-lg mt-4" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-dashed border-line py-16 text-center">
              <p className="text-ink-400">
                {list.total === 0 ? "Talent pool is empty." : "No profiles match."}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((t) => (
                  <div
                    key={t.id}
                    className="bg-white rounded-2xl border border-line p-5 hover:shadow-lg hover:border-brand-200 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 font-black text-lg">
                          {t.name[0]}
                        </div>
                        <div>
                          <p className="font-bold text-ink-900">{t.name}</p>
                          <p className="text-xs text-ink-400">{t.email}</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-ink-400 bg-surface-alt px-2 py-1 rounded-lg">
                        {fmtDate(t.submittedAt)}
                      </span>
                    </div>
                    <div className="flex gap-3 text-xs text-ink-600 mb-3 flex-wrap">
                      {t.location && <p>📍 {t.location}</p>}
                      {[t.preferredCity1, t.preferredCity2, t.preferredCity3].filter(Boolean)
                        .length > 0 ? (
                        <p className="text-brand-700 font-medium">
                          Pref:{" "}
                          {[t.preferredCity1, t.preferredCity2, t.preferredCity3]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      ) : null}
                      {t.source ? (
                        <p className="text-brand-700 font-semibold">
                          {TP_SOURCE_OPTIONS.find((o) => o.value === t.source)?.label ||
                            t.source}
                        </p>
                      ) : null}
                      <p>💼 {t.experience} yrs</p>
                      {t.cvFile || t.hasCv ? (
                        <p>
                          {fileIcon(t.cvFile?.ext || "pdf")}{" "}
                          {(t.cvFile?.ext || "pdf").toUpperCase()}
                        </p>
                      ) : (
                        <p className="text-ink-400">No CV</p>
                      )}
                    </div>
                    {t.desiredRoles?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mb-2">
                        {t.desiredRoles.slice(0, 3).map((r) => (
                          <span
                            key={r}
                            className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-lg"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    )}
                    {t.skills?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mb-3">
                        {t.skills.slice(0, 4).map((s) => (
                          <span
                            key={s}
                            className="text-xs bg-slate-100 text-ink-600 px-2 py-0.5 rounded-lg"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 pt-3 border-t border-line">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => view(t)}
                      >
                        View
                      </Button>
                      <button
                        onClick={() => download(t)}
                        disabled={!hasCv(t)}
                        aria-label="Download CV"
                        title={hasCv(t) ? "Download CV" : "No CV on file"}
                        className="px-3 py-2 border border-line text-ink-600 text-xs font-bold rounded-control disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                      >
                        ⬇
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {list.totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-8">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    ← Previous
                  </Button>
                  <span className="text-sm text-ink-600">
                    Page {page} of {list.totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={page >= list.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next →
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
